import { useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import type { OpenWorkRequestRow } from "@iamu/db";

export type QueueInput = {
  kind: "fix" | "verify" | "run";
  canonicalId?: string;
  scenarioId?: string;
  targetUrl?: string;
  note?: string;
};

export function useWorkRequests(projectId: string) {
  const utils = trpc.useUtils();
  const list = trpc.workRequest.list.useQuery(
    { projectId },
    { staleTime: 15_000, refetchInterval: 30_000 },
  );
  const [pendingSupersede, setPendingSupersede] = useState<{
    input: QueueInput;
    existing: OpenWorkRequestRow;
  } | null>(null);

  const refresh = () => {
    utils.workRequest.list.invalidate({ projectId });
    utils.workRequest.listForAgent.invalidate({ projectId });
    utils.finding.queue.invalidate({ projectId });
    utils.workflow.projectActivity.invalidate({ projectId });
  };

  const create = trpc.workRequest.create.useMutation({
    onSuccess: (res, vars) => {
      if ("existing" in res) {
        const existing =
          list.data?.find((r) => r.id === res.existing.id) ?? null;
        if (existing) {
          setPendingSupersede({ input: vars as QueueInput, existing });
        } else {
          toast.info("A request for this is already open");
        }
        return;
      }
      toast.success(
        `${vars.kind === "fix" ? "Fix" : vars.kind === "verify" ? "Verify" : "Run"} queued`,
      );
      refresh();
    },
    onError: (err) => toastTrpcError(err, "Failed to queue request"),
  });
  const cancelMutation = trpc.workRequest.cancel.useMutation({
    onSuccess: () => {
      toast.success("Request cancelled");
      refresh();
    },
    onError: (err) => toastTrpcError(err, "Failed to cancel request"),
  });
  const releaseMutation = trpc.workRequest.releaseLease.useMutation({
    onSuccess: () => {
      toast.success("Lease released — re-queued for next agent");
      refresh();
    },
    onError: (err) => toastTrpcError(err, "Failed to release lease"),
  });
  const bulkCreateMutation = trpc.workRequest.createBulk.useMutation({
    onSuccess: (res) => {
      toast.success(
        `Dispatched ${res.count} ${res.count === 1 ? "task" : "tasks"} to agent queue`,
      );
      refresh();
    },
    onError: (err) => toastTrpcError(err, "Failed to dispatch tasks"),
  });

  const byCanonical = useMemo(
    () =>
      new Map(
        (list.data ?? [])
          .filter((r) => r.canonicalId)
          .map((r) => [r.canonicalId!, r]),
      ),
    [list.data],
  );
  const byScenario = useMemo(
    () =>
      new Map(
        (list.data ?? [])
          .filter((r) => !r.canonicalId && r.scenarioId)
          .map((r) => [r.scenarioId!, r]),
      ),
    [list.data],
  );

  return {
    rows: list.data ?? [],
    isLoading: list.isLoading,
    refetch: list.refetch,
    byCanonical,
    byScenario,
    queue: (input: QueueInput) => create.mutate({ projectId, ...input }),
    queueFix: (canonicalId: string, note?: string) =>
      create.mutate({ projectId, kind: "fix", canonicalId, note }),
    queueVerify: (
      canonicalId: string,
      scenarioId?: string,
      targetUrl?: string,
      note?: string,
    ) =>
      create.mutate({
        projectId,
        kind: "verify",
        canonicalId,
        scenarioId,
        targetUrl,
        note,
      }),
    queueBulk: (canonicalIds: string[], kind: "fix" | "verify" = "fix") =>
      bulkCreateMutation.mutate({ projectId, canonicalIds, kind }),
    confirmSupersede: () => {
      if (!pendingSupersede) return;
      create.mutate({ projectId, ...pendingSupersede.input, supersede: true });
      setPendingSupersede(null);
    },
    pendingSupersede,
    dismissSupersede: () => setPendingSupersede(null),
    cancel: (id: string) => cancelMutation.mutate({ projectId, id }),
    releaseLease: (id: string) => releaseMutation.mutate({ projectId, id }),
    isPending:
      create.isPending ||
      cancelMutation.isPending ||
      releaseMutation.isPending ||
      bulkCreateMutation.isPending,
  };
}
